import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '../../../../../app/store/utils/baseQueryWithReauth';
import {
  SYSTEM_BILLING_BASE,
  unwrapApiData,
} from '../../../shared/infrastructure/api/unwrapApiData';
import type {
  CreatePaymentOptionInput,
  PaymentOption,
  PaymentOptionListItem,
  UpdatePaymentOptionInput,
} from '../../domain/payment-option.types';

export const billingPaymentOptionsApi = createApi({
  reducerPath: 'billingPaymentOptionsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BillingPaymentOption'],
  endpoints: build => ({
    getPaymentOptions: build.query<PaymentOptionListItem[], void>({
      query: () => ({
        url: `${SYSTEM_BILLING_BASE}/payment-options`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<PaymentOptionListItem[]>(response),
      providesTags: ['BillingPaymentOption'],
    }),

    getPaymentOptionById: build.query<PaymentOption, number>({
      query: id => ({
        url: `${SYSTEM_BILLING_BASE}/payment-options/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapApiData<PaymentOption>(response),
      providesTags: (_result, _error, id) => [{ type: 'BillingPaymentOption', id }],
    }),

    createPaymentOption: build.mutation<PaymentOption, CreatePaymentOptionInput>({
      query: body => ({
        url: `${SYSTEM_BILLING_BASE}/payment-options`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapApiData<PaymentOption>(response),
      invalidatesTags: ['BillingPaymentOption'],
    }),

    updatePaymentOption: build.mutation<
      PaymentOption,
      { id: number; body: UpdatePaymentOptionInput }
    >({
      query: ({ id, body }) => ({
        url: `${SYSTEM_BILLING_BASE}/payment-options/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => unwrapApiData<PaymentOption>(response),
      invalidatesTags: (_result, _error, { id }) => [
        'BillingPaymentOption',
        { type: 'BillingPaymentOption', id },
      ],
    }),
  }),
});

export const {
  useGetPaymentOptionsQuery,
  useGetPaymentOptionByIdQuery,
  useCreatePaymentOptionMutation,
  useUpdatePaymentOptionMutation,
} = billingPaymentOptionsApi;
